import { supabase } from '@/integrations/supabase/client';

export interface SupabaseBlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  content: string;
  author_name: string;
  featured_image_url: string | null;
  featured_image_alt: string | null;
  categories: string[];
  tags: string[];
  status: 'draft' | 'published' | 'archived' | 'scheduled';
  seo_meta: any;
  custom_fields?: any;
  published_at: string | null;
  scheduled_publish_at: string | null;
  updated_at: string;
  created_at: string;
  view_count: number;
}

export interface BlogPostsParams {
  page?: number;
  per_page?: number;
  search?: string;
  categories?: string;
  orderby?: string;
  order?: 'asc' | 'desc';
  status?: 'published' | 'draft' | 'archived' | 'scheduled' | 'all';
}

export interface BlogStats {
  totalPosts: number;
  publishedCount: number;
  draftCount: number;
  archivedCount: number;
  totalViews: number;
}

export interface BlogPostsResponse {
  posts: SupabaseBlogPost[];
  totalCount: number;
}

class SupabaseBlogService {
  async fetchPosts(params: BlogPostsParams = {}): Promise<BlogPostsResponse> {
    const {
      page = 1,
      per_page = 10,
      search,
      categories,
      orderby = 'published_at',
      order = 'desc',
      status = 'published'
    } = params;

    // Build base query for filtering
    let baseQuery = supabase
      .from('blog_posts')
      .select('*', { count: 'exact' });

    // Status filter - for public 'published' queries, also include scheduled posts whose time has passed
    if (status === 'published') {
      baseQuery = baseQuery.or(`status.eq.published,and(status.eq.scheduled,scheduled_publish_at.lte.${new Date().toISOString()})`);
    } else if (status !== 'all') {
      baseQuery = baseQuery.eq('status', status);
    }

    // Search
    if (search) {
      baseQuery = baseQuery.or(`title.ilike.%${search}%,excerpt.ilike.%${search}%,content.ilike.%${search}%`);
    }

    // Category filter
    if (categories) {
      const categoryList = categories.split(',');
      baseQuery = baseQuery.contains('categories', categoryList);
    }

    // Ordering
    baseQuery = baseQuery.order(orderby, { ascending: order === 'asc' });

    // Pagination
    const from = (page - 1) * per_page;
    const to = from + per_page - 1;
    const query = baseQuery.range(from, to);

    const { data, error, count } = await query;

    if (error) {
      console.error('Error fetching blog posts:', error);
      throw error;
    }

    return {
      posts: (data || []) as SupabaseBlogPost[],
      totalCount: count || 0
    };
  }

  async fetchPost(slug: string): Promise<SupabaseBlogPost | null> {
    // Fetch published posts, or scheduled posts whose publish time has passed
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('slug', slug)
      .or(`status.eq.published,and(status.eq.scheduled,scheduled_publish_at.lte.${new Date().toISOString()})`)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Not found
      }
      console.error('Error fetching blog post:', error);
      throw error;
    }

    // Increment view count
    if (data) {
      await this.incrementViewCount(data.id);
    }

    return data as SupabaseBlogPost;
  }

  async incrementViewCount(postId: string): Promise<void> {
    const { error } = await supabase.rpc('increment_blog_post_views', {
      post_id: postId
    });

    if (error) {
      console.error('Error incrementing view count:', error);
    }
  }

  async fetchStats(): Promise<BlogStats> {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('status, view_count');

    if (error) {
      console.error('Error fetching blog stats:', error);
      return {
        totalPosts: 0,
        publishedCount: 0,
        draftCount: 0,
        archivedCount: 0,
        totalViews: 0
      };
    }

    const stats = {
      totalPosts: data?.length || 0,
      publishedCount: data?.filter(p => p.status === 'published').length || 0,
      draftCount: data?.filter(p => p.status === 'draft').length || 0,
      archivedCount: data?.filter(p => p.status === 'archived').length || 0,
      scheduledCount: data?.filter(p => p.status === 'scheduled').length || 0,
      totalViews: data?.reduce((sum, p) => sum + (p.view_count || 0), 0) || 0
    };

    return stats;
  }

  async fetchCategories(): Promise<{ name: string; count: number }[]> {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('categories')
      .eq('status', 'published');

    if (error) {
      console.error('Error fetching categories:', error);
      return [];
    }

    // Flatten and count categories
    const categoryCounts: Record<string, number> = {};
    data?.forEach((post) => {
      post.categories?.forEach((category: string) => {
        categoryCounts[category] = (categoryCounts[category] || 0) + 1;
      });
    });

    return Object.entries(categoryCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }

  getFeaturedImageUrl(post: SupabaseBlogPost): string | null {
    return post.featured_image_url;
  }

  stripHtml(html: string): string {
    return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  }

  async createPost(data: {
    title: string;
    slug: string;
    content: string;
    excerpt?: string;
    author_name?: string;
    featured_image_url?: string;
    featured_image_alt?: string;
    categories?: string[];
    tags?: string[];
    status?: 'draft' | 'published' | 'scheduled';
    seo_meta?: any;
    custom_fields?: any;
    published_at?: string;
    scheduled_publish_at?: string | null;
  }): Promise<SupabaseBlogPost> {
    console.log('=== CREATE POST via Edge Function START ===');
    
    // 1. Verify session and get JWT token
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      console.error('❌ Session error:', sessionError);
      throw new Error('Authentication required to create blog post');
    }

    console.log('✅ Session verified, calling Edge Function with JWT');

    // 2. Prepare post data for Edge Function
    const postData = {
      title: data.title,
      slug: data.slug,
      content: data.content,
      excerpt: data.excerpt,
      author_name: data.author_name || 'SeniorThrive Team',
      status: data.status || 'draft',
      featured_image_url: data.featured_image_url,
      featured_image_alt: data.featured_image_alt,
      categories: data.categories || [],
      tags: data.tags || [],
      seo_meta: data.seo_meta || {},
      custom_fields: data.custom_fields || {},
      published_at: data.status === 'published' ? new Date().toISOString() : null,
      scheduled_publish_at: data.scheduled_publish_at || null,
    };

    console.log('📝 Calling Edge Function with post data:', {
      title: postData.title,
      slug: postData.slug,
      status: postData.status
    });

    // 3. Call Edge Function with JWT token
    const { data: result, error: invokeError } = await supabase.functions.invoke('insert-blog-post', {
      body: postData,
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (invokeError) {
      console.error('❌ Edge Function invocation error:', invokeError);
      throw new Error(`Failed to create blog post: ${invokeError.message}`);
    }

    if (!result?.success) {
      console.error('❌ Edge Function returned error:', result?.error);
      throw new Error(`Failed to create blog post: ${result?.error || 'Unknown error'}`);
    }

    console.log('✅ Blog post created successfully via Edge Function:', {
      id: result.data.id,
      slug: result.data.slug,
      title: result.data.title
    });
    console.log('=== CREATE POST via Edge Function END ===');
    
    return result.data as SupabaseBlogPost;
  }

  async updatePost(id: string, data: Partial<{
    title: string;
    slug: string;
    content: string;
    excerpt: string;
    author_name: string;
    featured_image_url: string;
    featured_image_alt: string;
    categories: string[];
    tags: string[];
    status: 'draft' | 'published' | 'archived' | 'scheduled';
    seo_meta: any;
    custom_fields: any;
    published_at: string;
    scheduled_publish_at: string | null;
  }>): Promise<SupabaseBlogPost> {
    // Verify session before updating post
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      console.error('Session error:', sessionError);
      throw new Error('Failed to verify authentication session');
    }
    
    if (!session) {
      console.error('No active session found');
      throw new Error('You must be logged in to update blog posts');
    }
    
    console.log('Updating blog post with authenticated user:', {
      userId: session.user.id,
      email: session.user.email,
      postId: id
    });
    
    const updateData: any = { ...data };
    
    // Set published_at when publishing for the first time
    if (data.status === 'published' && !data.published_at) {
      updateData.published_at = new Date().toISOString();
    }

    // ⭐ CRITICAL FIX: Explicitly set updated_by_user_id
    updateData.updated_by_user_id = session.user.id;

    const { data: post, error } = await supabase
      .from('blog_posts')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating blog post:', error);
      throw error;
    }

    return post as SupabaseBlogPost;
  }

  async deletePost(id: string): Promise<void> {
    const { error } = await supabase
      .from('blog_posts')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting blog post:', error);
      throw error;
    }
  }

  async fetchPostById(id: string): Promise<SupabaseBlogPost | null> {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      console.error('Error fetching blog post by ID:', error);
      throw error;
    }

    return data as SupabaseBlogPost;
  }
}

export const supabaseBlogService = new SupabaseBlogService();
